import { on } from 'embo/utils/events'
import vsync from 'embo/utils/vsync'

const CLICK_THRESHOLD = 5 // px, below this a pointerdown+up is treated as a click, not a drag

/**
 * Makes horizontally-overflowing elements matching `selector` scrollable by
 * mouse drag (like touch) and by mouse wheel (vertical wheel moves the
 * element horizontally).
 *
 * Uses event delegation on document.body, so it keeps working after the
 * matched elements are replaced by AJAX page transitions (see routing/index.js).
 *
 * @param {string} selector
 */
export default function initializeDragScroll(selector) {
  let target = null
  let dragging = false
  let moved = false
  let startX = 0
  let startScrollLeft = 0

  on(document.body, 'pointerdown', selector, ({event, target: el}) => {
    if (event.pointerType === 'touch' || (event.button !== undefined && event.button !== 0)) {
      // let native touch scrolling happen, only hijack the primary mouse button
      return
    }
    target = el
    dragging = true
    moved = false
    startX = event.clientX
    startScrollLeft = el.scrollLeft
    target.classList.add('is-dragging')
  })

  document.addEventListener('pointermove', event => {
    if (!dragging || !target) return
    const dx = event.clientX - startX
    if (Math.abs(dx) > CLICK_THRESHOLD) moved = true
    vsync.mutate(() => target.scrollLeft = startScrollLeft - dx)
  })

  const stopDragging = () => {
    if (!dragging) return
    dragging = false
    target.classList.remove('is-dragging')
  }
  document.addEventListener('pointerup', stopDragging)
  document.addEventListener('pointercancel', stopDragging)

  // A drag that actually moved the element shouldn't also trigger a click
  // (e.g. navigating a year link that was dragged past).
  on(document.body, 'click', `${selector} a`, ({event}) => {
    if (moved) {
      event.preventDefault()
      event.stopPropagation()
    }
  })

  on(document.body, 'wheel', selector, ({event, target: el}) => {
    if (el.scrollWidth <= el.clientWidth) return
    // hijack the whole wheel gesture: never let it chain up to a page scroll
    event.preventDefault()
    vsync.mutate(() => el.scrollLeft += event.deltaY + event.deltaX)
  })
}
